import { FEATURES_LIST } from '../constants';
import {
  FEATURES,
  getUwActiveFeatures,
  toggleFeature,
} from './features-mapping';
import { useStatistics } from './use-statistics';
import { getDifference } from '../utils/array-diff';

export interface HandleFeatures {
  activeFeatures: string[];
  activatedFeature: string;
  getFeatureButtonByName: (featureName: string) => HTMLButtonElement | null;
}

const ACTIVE_CLASS = 'active';

const toggleFeatureClass = (target: HTMLButtonElement) => {
  target.classList.toggle(ACTIVE_CLASS);
};

const hasActiveFeatureClass = (target: HTMLButtonElement) => {
  return target.classList.contains(ACTIVE_CLASS);
};

const featuresState = (featuresList: string[], feature: string) => {
  const isFeatureActive = featuresList.includes(feature);
  const removeFeature = (featureKey: string) =>
    featuresList.filter((activeFeature) => activeFeature !== featureKey);

  switch (feature) {
    case FEATURES.DARK_CONTRAST: {
      if (isFeatureActive) {
        return removeFeature(FEATURES.DARK_CONTRAST);
      }

      return removeFeature(FEATURES.INVERT_COLORS).concat(
        FEATURES.DARK_CONTRAST,
      );
    }
    case FEATURES.INVERT_COLORS: {
      if (isFeatureActive) {
        return removeFeature(FEATURES.INVERT_COLORS);
      }

      return removeFeature(FEATURES.DARK_CONTRAST).concat(
        FEATURES.INVERT_COLORS,
      );
    }
    case FEATURES.RESET: {
      return [];
    }
    default: {
      if (isFeatureActive) {
        return removeFeature(feature);
      } else {
        return featuresList.concat(feature);
      }
    }
  }
};

export const synchronizeState = ({
  getFeatureButtonByName,
}: {
  getFeatureButtonByName: (featureName: string) => HTMLButtonElement | null;
}) => {
  const activeFeatures = getUwActiveFeatures();

  activeFeatures.forEach((featureName) => {
    const feature = getFeatureButtonByName(featureName);
    if (!feature) {
      console.error(
        `Feature "${featureName}" is not found but has active state`,
      );
      return;
    }

    toggleFeatureClass(feature);
  });

  return activeFeatures;
};

export function handleFeatures({
  activeFeatures,
  activatedFeature,
  getFeatureButtonByName,
}: HandleFeatures) {
  const updatedState = featuresState(activeFeatures, activatedFeature);
  const { onFeatureTrigger } = useStatistics();

  const stateDifference = [
    ...getDifference(updatedState, activeFeatures),
    ...getDifference(activeFeatures, updatedState),
  ];

  stateDifference.forEach((featureName) => {
    const feature = getFeatureButtonByName(featureName);
    if (!feature) {
      console.error(`Feature "${featureName}" is not found but was called`);
      return;
    }

    // We should toggle only one activated feature, as related features switching implemented in the uw logic under the hood
    // Or all features in case "Reset All" feature was called.
    if (
      featureName === activatedFeature ||
      activatedFeature === FEATURES.RESET
    ) {
      const isActive = hasActiveFeatureClass(feature);
      const nextState = !isActive;

      toggleFeature({ name: featureName, isActive: nextState });

      window.postMessage(
        {
          isUserWay: true,
          action: 'sendEventToGoogleAnalytics',
          name: featureName.toLowerCase().replace(/ /g, '_'),
          state: nextState ? 1 : 0,
        },
        '*',
      );

      onFeatureTrigger(
        activatedFeature as keyof typeof FEATURES_LIST,
        nextState ? 1 : 0,
      );
    }

    toggleFeatureClass(feature);
  });

  return updatedState;
}
